import East from '@mui/icons-material/East';
import {
    Box,
    BoxProps,
    ButtonProps,
    Grid,
    LinkProps,
    LinkTypeMap,
    Stack,
    StackProps,
    TypographyProps
} from '@mui/material';
import React, { ElementType, ReactNode } from 'react';
import { filterStrapiContentArray, StrapiContentStatus } from '../../shared/utils';
import { LinkGroup, LinkGroupProps } from '../linkGroup';

export type LinkListProps = {
    links: LinkGroupProps[] | null | undefined
    withDropdown?: boolean
    alignLeft?: boolean
    allowGrid?: boolean
    forceLink?: boolean
    // forceButton?: boolean
    hidden?: boolean
    requiredPublishType?: StrapiContentStatus
    StackProps?: StackProps
    LinkGroupItemsStackProps?: StackProps
    relativeLinkComponent?: ElementType;
    headerComponent?: string
    ButtonProps?: ButtonProps
    BoxContainerProps?: BoxProps
    LinkProps?: LinkProps
    LinkGroupItemsTypographyProps?: TypographyProps
    LinkGroupHeaderTypographyProps?: TypographyProps
    LinkGroupHeaderLabelToIconMapping?: Record<string, ReactNode>
    LinkGroupHeaderLinkProps?: LinkTypeMap['props']
    onClick?: () => void
}

type LinkListItemProps = Omit<LinkListProps, 'links' | 'StackProps'> & {
    link: LinkGroupProps
}

const LinkListItem = ({
    link,
    withDropdown,
    alignLeft,
    relativeLinkComponent,
    headerComponent,
    forceLink,
    // forceButton,
    LinkProps,
    LinkGroupItemsTypographyProps,
    LinkGroupHeaderLinkProps,
    LinkGroupHeaderTypographyProps,
    LinkGroupHeaderLabelToIconMapping,
    LinkGroupItemsStackProps,
    ButtonProps,
    requiredPublishType,
    onClick
}: LinkListItemProps): React.ReactNode => {
    if (!link) return null

    return (
        <LinkGroup
            label={link.label}
            url={link.url}
            buttonType={link.buttonType}
            buttonColor={link.buttonColor}
            icon={link.icon}
            // forceButton={forceButton}
            requiredPublishType={requiredPublishType}
            description={link.description}
            links={link.links}
            onClick={onClick || link.onClick}
            ButtonProps={ButtonProps || link.ButtonProps}
            withDropdown={withDropdown || link.withDropdown}
            LinkGroupHeaderLabelToIconMapping={LinkGroupHeaderLabelToIconMapping || link.LinkGroupHeaderLabelToIconMapping}
            LinkGroupItemsStackProps={LinkGroupItemsStackProps || link.LinkGroupItemsStackProps}
            alignLeft={alignLeft || link.alignLeft}
            forceLink={forceLink || link.forceLink}
            headerComponent={headerComponent || link.headerComponent}
            LinkProps={LinkProps || link.LinkProps}
            LinkGroupHeaderTypographyProps={LinkGroupHeaderTypographyProps || link.LinkGroupHeaderTypographyProps}
            LinkGroupItemsTypographyProps={LinkGroupItemsTypographyProps || link.LinkGroupItemsTypographyProps}
            LinkGroupHeaderLinkProps={LinkGroupHeaderLinkProps || link.LinkGroupHeaderLinkProps}
            relativeLinkComponent={relativeLinkComponent || link.relativeLinkComponent}
        />
    )
}

export function LinkList({
    links,
    StackProps,
    allowGrid,
    requiredPublishType,
                             BoxContainerProps,
    ...other
}: LinkListProps) {
    if (!links) return <></>
    const filtered = filterStrapiContentArray(requiredPublishType, links);
    if (!filtered || filtered.length === 0) return <></>

    if (allowGrid && filtered.length > 3) {
        return (
            <Grid spacing={{ xs: 3, sm: 3, md: 4 }} {...StackProps} container>
                {filtered.map((link, i) => {
                    return (
                        <Grid
                            size={{ xs: 12, sm: 12, md: 4 }}
                            key={i}>
                            <Box sx={{ maxWidth: { xs: '100%', sm: '100%', md: '270px' } }}>
                                <LinkListItem
                                    {...other}
                                    link={link}
                                    requiredPublishType={requiredPublishType}
                                    ButtonProps={{
                                        ...other.ButtonProps,
                                        endIcon: other.ButtonProps?.endIcon || <East />,
                                        size: other.ButtonProps?.size || 'small',
                                        color: other.ButtonProps?.color || 'primary',
                                        sx: {
                                            fontSize: '0.8rem',
                                            lineHeight: '1rem',
                                            ':hover': {
                                                textDecoration: 'underline',
                                            },
                                            textDecoration: 'none!important',
                                            ...other.ButtonProps?.sx
                                        }
                                    }}
                                />
                            </Box>
                        </Grid>
                    )
                })}
            </Grid>
        )
    }

    return (
        <Stack gap={{ xs: 2, md: 4, lg: 6 }} {...StackProps}>
            {filtered.map((link, i) => {
                return (
                    <Box key={i} {...BoxContainerProps}>
                        <LinkListItem
                            {...other}
                            requiredPublishType={requiredPublishType}
                            ButtonProps={{ ...other.ButtonProps, ...link.ButtonProps }}
                            link={link}
                        />
                    </Box>
                )
            })}
        </Stack>
    )
}
